/**
 * Static career-resources content — long-form pages for SEO.
 * Add new entries here; they automatically appear on `/career-resources`
 * and become live at `/career-resources/<slug>`.
 *
 * Content is a lightweight array of blocks so we don't need a markdown
 * parser on the frontend and pages stay accessible/ATS-safe.
 *   { type: 'p'    , text: string }
 *   { type: 'h2'   , text: string }
 *   { type: 'ul'   , items: string[] }
 *   { type: 'quote', text: string, cite?: string }
 */

export const CAREER_RESOURCES = [
  {
    slug: "how-to-write-a-uk-cv",
    title: "How to write a UK CV that actually gets interviews",
    excerpt:
      "The five sections UK recruiters expect, the mistakes that get you filtered out, and how to structure each block for maximum impact.",
    read_min: 6,
    updated: "March 2026",
    hero_stat: "90%",
    hero_stat_label: "of employers use ATS to filter CVs",
    tags: ["CV writing", "UK jobs", "ATS"],
    blocks: [
      {
        type: "p",
        text:
          "A UK CV is not a US resume. It's typically two pages, chronological, written in UK English, and expected to include personal contact details, a professional summary, core skills, work experience, education and any relevant certifications. Get any of these wrong and you're likely to be filtered out before a human ever sees your application.",
      },
      { type: "h2", text: "1. The header — get discoverable" },
      {
        type: "p",
        text:
          "At the top of the page: your full name, city (not full address), phone number, professional email address and a LinkedIn URL. Skip photos, marital status and date of birth — they're not the norm in the UK and can trigger unconscious bias filtering.",
      },
      { type: "h2", text: "2. Professional summary — earn the read" },
      {
        type: "p",
        text:
          "Three to four sentences that answer 'who are you, what do you do brilliantly, and what are you looking for next?'. Recruiters spend 7 seconds on this section. Make every word matter.",
      },
      { type: "h2", text: "3. Core skills — pass the ATS scan" },
      {
        type: "p",
        text:
          "8–12 keyword-rich skills, mirrored from the job spec. This is where the Applicant Tracking System scores you. If the job says 'stakeholder management', put those exact words.",
      },
      { type: "h2", text: "4. Experience — quantify everything" },
      {
        type: "p",
        text:
          "For each role: strong action verb + specific achievement + measurable outcome. 'Led a 5-person analytics squad delivering £1.2M in cost savings' beats 'Responsible for analytics' every single time.",
      },
      {
        type: "ul",
        items: [
          "Start each bullet with a verb: Led, Delivered, Built, Cut, Grew.",
          "Include a number in at least 60% of your bullets.",
          "Group achievements under each role — not one giant list at the top.",
        ],
      },
      { type: "h2", text: "5. Education & certifications — keep it lean" },
      {
        type: "p",
        text:
          "Degree, institution, year. If you graduated more than 10 years ago, drop the grade and dates. Keep certifications directly relevant to the role you're targeting.",
      },
      {
        type: "quote",
        text:
          "A great CV isn't the one that lists everything you've ever done — it's the one that tells the story of the role you're about to get.",
      },
    ],
  },
  {
    slug: "ats-explained",
    title: "ATS explained — how UK employers actually filter CVs",
    excerpt:
      "Applicant Tracking Systems shortlist most applications before a human sees them. Here's what they check and how to pass every time.",
    read_min: 5,
    updated: "March 2026",
    hero_stat: "75%",
    hero_stat_label: "of CVs are rejected by ATS before a human reads them",
    tags: ["ATS", "CV writing"],
    blocks: [
      {
        type: "p",
        text:
          "An Applicant Tracking System (ATS) is software that ranks and filters CVs based on the job spec. If your CV isn't formatted for machines, it never reaches a human — no matter how strong your experience is.",
      },
      { type: "h2", text: "What ATS looks for" },
      {
        type: "ul",
        items: [
          "Keyword match to the job description (usually 60–70% overlap).",
          "Standard section headings: Summary, Experience, Education, Skills.",
          "Simple structure — no columns, tables, text boxes or images.",
          "Consistent date formats (Jan 2022 – Present).",
          "Common fonts: Arial, Calibri, Georgia.",
        ],
      },
      { type: "h2", text: "The 5 formatting mistakes that break ATS" },
      {
        type: "ul",
        items: [
          "Two-column CVs — most ATS reads left-to-right and mixes the columns.",
          "Text inside images (icons for skills, logos) — invisible to ATS.",
          "Headers/footers with contact details — often stripped out.",
          "PDFs with unusual fonts or heavy graphics.",
          "Creative section names ('My Journey', 'What I Bring') — ATS looks for standard labels.",
        ],
      },
      { type: "h2", text: "How to check your CV's ATS score" },
      {
        type: "p",
        text:
          "Paste your CV into a plain text editor. If it looks scrambled, ATS will too. Then compare its keywords against the job spec you're targeting.",
      },
      {
        type: "quote",
        text:
          "ATS isn't your enemy — it's a filter. Write for it, and you'll clear the first gate on every application.",
      },
    ],
  },
  {
    slug: "cover-letter-tips",
    title: "Cover letter tips — what UK recruiters actually read",
    excerpt:
      "A cover letter isn't optional in the UK. Here's how to write one that adds signal, not noise — in 220 words.",
    read_min: 4,
    updated: "March 2026",
    hero_stat: "~250",
    hero_stat_label: "words — the ideal UK cover letter length",
    tags: ["Cover letter", "UK jobs"],
    blocks: [
      {
        type: "p",
        text:
          "A UK cover letter is short, punchy and shows the reader — a busy hiring manager — three things: you understand the role, you have the specific track record for it, and you're a person they'd want on their team.",
      },
      { type: "h2", text: "The 4-paragraph structure" },
      {
        type: "ul",
        items: [
          "Paragraph 1 — Who you are and which specific role you're applying to.",
          "Paragraph 2 — One concrete achievement that maps directly to the job.",
          "Paragraph 3 — Why THIS company (not any company like it).",
          "Paragraph 4 — Confident, short close — availability + call to action.",
        ],
      },
      { type: "h2", text: "Things to leave out" },
      {
        type: "ul",
        items: [
          "'I am a dynamic team player' and any cliché you've read on 100 other CVs.",
          "Your entire career story — that's what the CV is for.",
          "'To whom it may concern' — 'Dear Hiring Manager,' is fine when a name isn't public.",
          "Excessive formatting — plain paragraphs, no tables, no bullets.",
        ],
      },
      {
        type: "quote",
        text:
          "The best cover letters read like a short, confident recommendation from a friend — clear voice, specific proof, no fluff.",
      },
    ],
  },
  {
    slug: "linkedin-optimisation",
    title: "LinkedIn optimisation — get found by UK recruiters",
    excerpt:
      "The eight LinkedIn settings that decide whether recruiters find you. Every one takes under 10 minutes to fix.",
    read_min: 6,
    updated: "March 2026",
    hero_stat: "40x",
    hero_stat_label: "more recruiter views on optimised profiles",
    tags: ["LinkedIn", "UK jobs"],
    blocks: [
      {
        type: "p",
        text:
          "LinkedIn's Recruiter platform is where 87% of UK recruiters start their search. If your profile isn't optimised for it, you're invisible to the people making hiring decisions.",
      },
      { type: "h2", text: "The 8 essentials" },
      {
        type: "ul",
        items: [
          "Headline — role + speciality + keyword, not just 'Manager at X'.",
          "About — 3 short paragraphs with your one-liner, proof, and CTA.",
          "Experience — mirror your CV, keep bullets achievement-led.",
          "Skills — pin the 3 most important to your target role at the top.",
          "Location — set to the target city (London, Manchester, etc.).",
          "Open to work — turn on the recruiter-only badge.",
          "Custom URL — /in/yourname, not the auto-generated one.",
          "Photo + banner — professional, sector-appropriate, high-res.",
        ],
      },
      { type: "h2", text: "The one thing to do this week" },
      {
        type: "p",
        text:
          "Rewrite your headline. The default is 'Job title at Company' — replace it with '<Role> · <Speciality> · <Keyword>'. Example: 'Senior Data Analyst · Fintech · Python & SQL'. That single change usually 5-10x recruiter search impressions in a fortnight.",
      },
    ],
  },
];

export function findResource(slug) {
  return CAREER_RESOURCES.find((r) => r.slug === slug) || null;
}
